import { useState, useCallback, useEffect } from 'react';
import { Quotation, CreateQuotationData } from '../types/quotation';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp,
  serverTimestamp,
  onSnapshot,
  getDoc,
  getDocs,
  where
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { generateQuotationNumber } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { useNotifications } from './useNotifications';
import { useLanguage } from '../contexts/LanguageContext';

interface UseQuotationsReturn {
  quotations: Quotation[];
  loading: boolean;
  error: Error | null;
  createQuotation: (data: CreateQuotationData) => Promise<string>;
  updateQuotation: (id: string, data: CreateQuotationData) => Promise<void>;
  deleteQuotation: (id: string) => Promise<void>;
  duplicateQuotation: (id: string) => Promise<string>;
  refreshQuotations: () => Promise<void>;
}

export const useQuotations = (): UseQuotationsReturn => {
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  const { canAdmin, isSuperAdmin } = usePermissions();
  const { createNotification } = useNotifications();
  const { t } = useLanguage();

  // Real-time listener a quotations kollekcióra
  useEffect(() => {
    setLoading(true);
    const quotationsRef = collection(db, 'quotations');
    let q = query(quotationsRef, orderBy('createdAt', 'desc'));

    // A super_admin és admin minden ajánlatot láthat
    // Egyébként szűrünk organizationId alapján, ha van beállítva
    if (user?.role === "super_admin") {
      q = query(quotationsRef, orderBy('createdAt', 'desc'));
    } else if (!canAdmin('quotations') && user?.organizationId) {
      q = query(quotationsRef, where('organizationId', '==', user.organizationId), orderBy('createdAt', 'desc'));
    }

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const loadedQuotations = snapshot.docs.map(doc => {
          const data = doc.data();
          
          // Make sure both number and quotationNumber are properly set and include company name
          let number = data.number || '';
          let quotationNumber = data.quotationNumber || number;
          
          // If quotationNumber exists but doesn't match the full pattern (might be missing company name)
          // Try to extract company name from the number if it exists and is longer
          if (quotationNumber && number && number.split('-').length > quotationNumber.split('-').length) {
            // The number has more segments, likely includes company name
            quotationNumber = number;
          }
          
          // Both fields should always be set to the same value
          return {
            ...data,
            id: doc.id,
            number: number,
            quotationNumber: quotationNumber,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          };
        }) as Quotation[];

        setQuotations(loadedQuotations);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading quotations:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    // Cleanup subscription
    return () => unsubscribe();
  }, [user, canAdmin]);

  // Function to manually refresh quotations
  const refreshQuotations = async (): Promise<void> => {
    try {
      setLoading(true);
      const quotationsRef = collection(db, 'quotations');
      let q = query(quotationsRef, orderBy('createdAt', 'desc'));

      if (user?.role === "super_admin") {
        q = query(quotationsRef, orderBy('createdAt', 'desc'));
      } else if (!canAdmin('quotations') && user?.organizationId) {
        q = query(quotationsRef, where('organizationId', '==', user.organizationId), orderBy('createdAt', 'desc'));
      }

      const snapshot = await getDocs(q);
      
      const loadedQuotations = snapshot.docs.map(doc => {
        const data = doc.data();
        
        // Make sure both number and quotationNumber are properly set and include company name
        let number = data.number || '';
        let quotationNumber = data.quotationNumber || number;
        
        // If quotationNumber exists but doesn't match the full pattern (might be missing company name)
        // Try to extract company name from the number if it exists and is longer
        if (quotationNumber && number && number.split('-').length > quotationNumber.split('-').length) {
          // The number has more segments, likely includes company name
          quotationNumber = number;
        }
        
        // Both fields should always be set to the same value
        return {
          ...data,
          id: doc.id,
          number: number,
          quotationNumber: quotationNumber,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        };
      }) as Quotation[];

      setQuotations(loadedQuotations);
      setError(null);
    } catch (err) {
      console.error('Error refreshing quotations:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const createQuotation = async (data: CreateQuotationData): Promise<string> => {
    try {
      const lastQuotation = quotations[0];
      
      // Extract customer information to get company name
      let companyShortName = '';
      
      // If the data includes a quotationNumber that has company name, extract it
      if (data.quotationNumber && data.quotationNumber.includes('-')) {
        const parts = data.quotationNumber.split('-');
        if (parts.length >= 6) {
          companyShortName = parts[parts.length - 1];
          console.log('Using company name from quotationNumber:', companyShortName);
        }
      }
      
      // Generate number with company name, passing quotations array and customer ID
      const generatedNumber = generateQuotationNumber(
        lastQuotation?.number, 
        companyShortName,
        quotations,
        data.customerId
      );
      console.log('Generated quotation number in create:', generatedNumber);

      const newQuotation = {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        number: generatedNumber,
        quotationNumber: generatedNumber, // Always set both fields to the same value
        status: data.status || 'draft' as const,
        publicToken: crypto.randomUUID()
      };

      const docRef = await addDoc(collection(db, 'quotations'), newQuotation);

      // Értesítés létrehozása
      try {
        if (user?.organizationId) {
          const lang = t('common.language') === 'English' ? 'en' : 'hu';
          const translations = lang === 'en' 
            ? (await import('../translations/en')).default.notifications
            : (await import('../translations/hu')).default.notifications;
            
          const title = translations.subjects?.quotationCreated 
            ? translations.subjects.quotationCreated.replace('{{quotationNumber}}', generatedNumber)
            : `${lang === 'hu' ? 'Új ajánlat létrehozva' : 'New quotation created'}: ${generatedNumber}`;
            
          await createNotification({
            type: 'system',
            title: title,
            message: `${generatedNumber} ${lang === 'hu' ? 'számú ajánlat sikeresen létrehozva' : 'quotation successfully created'}.`,
            priority: 'medium',
            userId: user.id,
            organizationId: user.organizationId,
            createdBy: user.id,
            createdByName: user.displayName,
            actionUrl: '/quotations',
            data: { quotationId: docRef.id }
          });
        }
      } catch (notifError) {
        console.error('Error creating notification:', notifError);
      }

      return docRef.id;
    } catch (error) {
      console.error('Error creating quotation:', error);
      throw error;
    }
  };

  const updateQuotation = async (id: string, data: CreateQuotationData): Promise<void> => {
    try {
      const docRef = doc(db, 'quotations', id);
      const timestamp = serverTimestamp();
      
      // Make sure both number fields are correctly set
      let number = data.number || '';
      let quotationNumber = data.quotationNumber || number;
      
      // If quotationNumber doesn't match the full pattern, use number if it's more complete
      if (quotationNumber && number && number.split('-').length > quotationNumber.split('-').length) {
        quotationNumber = number;
      }
      
      // Both fields should be set to the same value that includes company name
      const updateData = {
        ...data,
        number: number,
        quotationNumber: quotationNumber,
        updatedAt: timestamp
      };

      await updateDoc(docRef, updateData);

      // Értesítés létrehozása ha státusz változott
      try {
        if (data.status && user?.organizationId) {
          const lang = t('common.language') === 'English' ? 'en' : 'hu';
          const translations = lang === 'en' 
            ? (await import('../translations/en')).default.notifications
            : (await import('../translations/hu')).default.notifications;
            
          const statusLabel = (translations.statuses as any)?.[data.status] || data.status;
          const priority = data.status === 'accepted' ? 'high' : data.status === 'rejected' ? 'medium' : 'low';
          
          const title = translations.subjects?.quotationStatusChanged 
            ? translations.subjects.quotationStatusChanged.replace('{{quotationNumber}}', number)
            : `${lang === 'hu' ? 'Ajánlat státusz változott' : 'Quotation status changed'}: ${number}`;
          
          await createNotification({
            type: 'system',
            title: title,
            message: `${number} ${lang === 'hu' ? 'számú ajánlat státusza' : 'quotation status'}: ${statusLabel}`,
            priority: priority as 'low' | 'medium' | 'high',
            userId: user.id,
            organizationId: user.organizationId,
            createdBy: user.id,
            createdByName: user.displayName,
            actionUrl: '/quotations',
            data: { quotationId: id }
          });
        }
      } catch (notifError) {
        console.error('Error creating notification:', notifError);
      }
    } catch (error) {
      console.error('Error updating quotation:', error);
      throw error;
    }
  };

  const deleteQuotation = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'quotations', id));
    } catch (error) {
      console.error('Error deleting quotation:', error);
      throw error;
    }
  };

  const duplicateQuotation = async (id: string): Promise<string> => {
    try {
      const docRef = doc(db, 'quotations', id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Quotation not found');
      }

      const originalData = docSnap.data();
      const timestamp = serverTimestamp();
      
      // Extract company short name - prefer quotationNumber if it exists and has company name
      let companyShortName = '';
      
      // First check the quotationNumber field
      if (originalData.quotationNumber && originalData.quotationNumber.includes('-')) {
        const parts = originalData.quotationNumber.split('-');
        if (parts.length >= 6) {
          // E.I.-VE-0301-A-18-Megat -> Megat is the last part
          companyShortName = parts[parts.length - 1];
          console.log('Extracted company name from quotationNumber:', companyShortName);
        }
      }
      
      // If not found, check the number field
      if (!companyShortName && originalData.number && originalData.number.includes('-')) {
        const parts = originalData.number.split('-');
        if (parts.length >= 6) {
          companyShortName = parts[parts.length - 1];
          console.log('Extracted company name from number:', companyShortName);
        }
      }
      
      // Generate a new quotation number that includes the company name
      const lastQuotation = quotations[0];
      const generatedNumber = generateQuotationNumber(
        lastQuotation?.number, 
        companyShortName,
        quotations,
        originalData.customerId
      );
      console.log('Generated new quotation number with company name:', generatedNumber);

      // Create a base object with all required fields
      const newQuotation: any = {
        // Basic required fields
        organizationId: originalData.organizationId,
        customerId: originalData.customerId,
        items: originalData.items || [],
        number: generatedNumber,
        quotationNumber: generatedNumber, // Set both fields to the same value
        status: 'draft' as const,
        createdAt: timestamp,
        updatedAt: timestamp,
        validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        
        // Company and contact related fields that should exist
        companyName: originalData.companyName || null,
        companyAddress: originalData.companyAddress || null,
        taxNumber: originalData.taxNumber || null,
        registrationNumber: originalData.registrationNumber || null,
        representativeName: originalData.representativeName || null,
        contactName: originalData.contactName || null,
        
        // Contract details
        contractStart: originalData.contractStart || null,
        contractEnd: originalData.contractEnd || null,
        
        // Price and consumption
        price: originalData.price || null,
        priceUnit: originalData.priceUnit || null,
        consumption: originalData.consumption || null,
        
        // POD information
        pod: originalData.pod || null,
        additionalPods: originalData.additionalPods || null,
        
        // Other fields
        kat: originalData.kat || null,
        notes: originalData.notes || null,
        documents: [], // Start with empty documents
        
        // Generate new public token
        publicToken: crypto.randomUUID()
      };

      // Only add firstName and lastName if they exist in the original data
      if (originalData.firstName !== undefined) {
        newQuotation.firstName = originalData.firstName;
      }
      
      if (originalData.lastName !== undefined) {
        newQuotation.lastName = originalData.lastName;
      }

      const newDocRef = await addDoc(collection(db, 'quotations'), newQuotation);
      return newDocRef.id;
    } catch (error) {
      console.error('Error duplicating quotation:', error);
      throw error;
    }
  };

  return {
    quotations,
    loading,
    error,
    createQuotation,
    updateQuotation,
    deleteQuotation,
    duplicateQuotation,
    refreshQuotations
  };
}; 